import React, { useEffect } from "react";
import Select from "react-select";
import { Button } from "antd";
import { MdCancel } from "react-icons/md";

const DEAL_DATE_FILTER_OPTIONS = [
  { value: "createdTime", label: "Created Time" },
  { value: "paymentDate", label: "Payment Date" },
];

const getDateFilterKeys = (module, dateFilterOn = "createdTime") => {
  if (module === "leads") {
    return {
      gte: "createdTime[gte]",
      lte: "createdTime[lte]",
    };
  }

  if (dateFilterOn === "paymentDate") {
    return {
      gte: "verificationRecord[paymentDate][gte]",
      lte: "verificationRecord[paymentDate][lte]",
    };
  }

  return {
    gte: "createdTime[gte]",
    lte: "createdTime[lte]",
  };
};

const clearDealDateFilters = (params) => {
  [
    "createdTime[gte]",
    "createdTime[lte]",
    "leadToDealOn[gte]",
    "leadToDealOn[lte]",
    "verificationRecord[paymentDate][gte]",
    "verificationRecord[paymentDate][lte]",
  ].forEach((key) => params.delete(key));
};

const ReportsModal = ({
  reportDataFilter,
  setReportDataFilter,
  handleDownload,
  module,
}) => {
  const fileTypeOptions = [{ value: "excel", label: "Download as Excel" }];

  // Inside the component
  useEffect(() => {
    const queryParams = reportDataFilter?.filters?.queryParams;

    const params = new URLSearchParams(queryParams || "");
    const activeDateFilterOn =
      module === "deals"
        ? reportDataFilter?.dateFilterOn || "createdTime"
        : "createdTime";
    const { gte: gteKey, lte: lteKey } = getDateFilterKeys(
      module,
      activeDateFilterOn
    );

    setReportDataFilter((prev) => ({
      ...prev,
      fromDate: params.get(gteKey) || "",
      toDate: params.get(lteKey) || "",
    }));
  }, [
    module,
    reportDataFilter?.dateFilterOn,
    reportDataFilter?.filters?.queryParams,
    setReportDataFilter,
  ]);

  const handleFileTypeChange = (selectedOption) => {
    setReportDataFilter((prev) => ({
      ...prev,
      format: selectedOption.value,
    }));
  };

  const isDateSelected = reportDataFilter.fromDate && reportDataFilter.toDate;

  return (
    <div className="modal custom-modal fade" id="download_report" role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Download Report</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <MdCancel size={50} />
            </button>
          </div>
          <div className="modal-body">
            <form>
              <div className="mb-3">
                <label className="form-label">
                  File Type <span className="text-danger">*</span>
                </label>
                <Select
                  classNamePrefix="react-select"
                  options={fileTypeOptions}
                  value={fileTypeOptions.find(
                    (option) => option.value === reportDataFilter.format
                  )}
                  onChange={handleFileTypeChange}
                  placeholder="Select File Type"
                />
              </div>

              {module === "deals" && (
                <div className="mb-3">
                  <label className="form-label">Date Filter On</label>
                  <div className="d-flex gap-3 flex-wrap">
                    {DEAL_DATE_FILTER_OPTIONS.map((option) => (
                      <div className="form-check" key={option.value}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="dealDateFilterOn"
                          id={`deal-date-filter-${option.value}`}
                          checked={reportDataFilter.dateFilterOn === option.value}
                          onChange={() => {
                            setReportDataFilter((prev) => {
                              const params = new URLSearchParams(
                                prev.filters?.queryParams || ""
                              );
                              clearDealDateFilters(params);

                              return {
                                ...prev,
                                dateFilterOn: option.value,
                                fromDate: "",
                                toDate: "",
                                filters: {
                                  ...prev.filters,
                                  queryParams: params.toString(),
                                },
                              };
                            });
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`deal-date-filter-${option.value}`}
                        >
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">From Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={reportDataFilter.fromDate}
                  onChange={(e) => {
                    const newFromDate = e.target.value;

                    setReportDataFilter((prev) => {
                      const params = new URLSearchParams(
                        prev.filters?.queryParams || ""
                      );
                      const { gte } = getDateFilterKeys(
                        module,
                        prev.dateFilterOn
                      );

                      if (module === "deals") {
                        clearDealDateFilters(params);
                        if (prev.toDate) {
                          const { lte } = getDateFilterKeys(
                            module,
                            prev.dateFilterOn
                          );
                          params.set(lte, prev.toDate);
                        }
                      }

                      params.set(gte, newFromDate);

                      return {
                        ...prev,
                        fromDate: newFromDate,
                        filters: {
                          ...prev.filters,
                          queryParams: params.toString(),
                        },
                      };
                    });
                  }}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">To Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={reportDataFilter.toDate}
                  onChange={(e) => {
                    const newToDate = e.target.value;

                    setReportDataFilter((prev) => {
                      const params = new URLSearchParams(
                        prev.filters?.queryParams || ""
                      );
                      const { lte } = getDateFilterKeys(
                        module,
                        prev.dateFilterOn
                      );

                      if (module === "deals") {
                        clearDealDateFilters(params);
                        if (prev.fromDate) {
                          const { gte } = getDateFilterKeys(
                            module,
                            prev.dateFilterOn
                          );
                          params.set(gte, prev.fromDate);
                        }
                      }

                      params.set(lte, newToDate);

                      return {
                        ...prev,
                        toDate: newToDate,
                        filters: {
                          ...prev.filters,
                          queryParams: params.toString(),
                        },
                      };
                    });
                  }}
                />
              </div>

              <div className="modal-btn d-flex justify-content-end align-items-center gap-2">
                <Button
                  type="default" // Use "default" for a secondary/cancel button
                  className="btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Button>
                <Button
                  type="primary" // Use "primary" for the main action button
                  onClick={handleDownload}
                  data-bs-dismiss="modal"
                  disabled={!isDateSelected} // Disable button if dates are not selected
                >
                  Download Now
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsModal;
